import { useRef, useState } from 'react';
import { Loader2, Mail, MessageSquare, Trash2 } from 'lucide-react';
import { Action, EmailNotification, SMSNotification } from '@the-real-insight/tri-model';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import AlertMessagePreview from './AlertMessagePreview';
import PlaceholderTextField, { PlaceholderTextFieldHandle } from './PlaceholderTextField';
import { PLACEHOLDER_ICONS, pickerLabelKeyOf } from './alertPlaceholderPicker';
import { iconMarkup } from './placeholderIcons';
import { CircleAlert, X } from 'lucide-react';
import { BadgeLook } from './placeholderDom';
import PlaceholderPicker from './PlaceholderPicker';
import {
    ALERT_TEXT_NAMESPACE,
    placeholderTag,
    placeholdersOf,
    unknownPlaceholders,
    UnknownPlaceholder,
} from './alertPlaceholders';

/**
 * `useTranslation`s `t`, so weit dieses Panel es braucht — auch mit Werten, weil die
 * Platzhalter-Meldungen den falschen und den gemeinten Namen einsetzen.
 *
 * Nicht `TFunction` selbst: Deren Überladung mit Werten liefert im Typ eine Vereinigung aus Text
 * und Detailobjekt (`returnDetails`), und dieses Panel will überall den Text.
 */
/** `count` is a number so that i18next picks the singular/plural key (`…_one` / `…_other`). */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** Der Fehlertext zu einem unbekannten Platzhalter — mit Vorschlag, wo es einen gibt. */
export const unknownPlaceholderMessage = (problem: UnknownPlaceholder, t: Translate): string =>
    problem.suggestion
        ? t(`${ALERT_TEXT_NAMESPACE}unknownPlaceholderSuggestion`, {
            unknown: placeholderTag(problem.name),
            suggestion: placeholderTag(problem.suggestion),
        })
        : t(`${ALERT_TEXT_NAMESPACE}unknownPlaceholder`, { unknown: placeholderTag(problem.name) });

/** Jeder unbekannte Platzhalter einer Aktion — Betreff und Rumpf zusammen, ohne Wiederholungen. */
export const unknownPlaceholdersOfAction = (action: any): UnknownPlaceholder[] => {
    const problems = [
        ...unknownPlaceholders(action?.subject),
        ...unknownPlaceholders(action?.content),
    ];

    return problems.filter(
        (problem, index) => problems.findIndex((other) => other.name === problem.name) === index,
    );
};

/** The badge of a token: friendly label, raw token as tooltip; unknown tokens red with the existing message. */
export const placeholderBadge = (name: string, t: Translate): BadgeLook => {
    const problem = unknownPlaceholders(placeholderTag(name))[0];

    if (problem) {
        return {
            label: placeholderTag(name),
            kind: 'unknown',
            iconHtml: iconMarkup(CircleAlert),
            removeHtml: iconMarkup(X, 12),
            removeLabel: t(`${ALERT_TEXT_NAMESPACE}removePlaceholder`, { label: placeholderTag(name) }),
            title: unknownPlaceholderMessage(problem, t),
        };
    }

    const info = pickerLabelKeyOf(name);

    const label = info ? t(`${ALERT_TEXT_NAMESPACE}picker.items.${info.key}.label`) : placeholderTag(name);

    return {
        removeHtml: iconMarkup(X, 12),
        removeLabel: t(`${ALERT_TEXT_NAMESPACE}removePlaceholder`, { label }),
        label,
        iconHtml: info ? iconMarkup(PLACEHOLDER_ICONS[info.key]) : undefined,
        kind: info?.legacy ? 'legacy' : 'known',
        title: placeholderTag(name),
    };
};

/** In welches Feld einer Aktion ein Platzhalter eingesetzt wird. */
type PlaceholderTarget = 'subject' | 'content';

/**
 * Eine Aktion bearbeiten: Empfänger, Betreff, Text — und was der Server daraus macht.
 *
 * Eigene Komponente, weil jede Aktion ihre eigenen Schreibmarken hat: Der Platzhalter-Knopf neben
 * einem Feld setzt dort ein, wo in genau diesem Feld zuletzt geschrieben wurde (die Auswahl eines
 * Eingabefelds überlebt den Fokuswechsel in die Liste), und dafür braucht jedes Feld seinen Verweis.
 */
export default function ActionEditor(properties: {
    t: Translate;
    action: Action;
    index: number;
    fieldId: string;
    testing: boolean;
    /** Whether the rule is a "days without uplink" rule — only then the picker offers uplink values. */
    noUplink: boolean;
    /** The alarm the preview shows when the rule is "any of the device's own alarms". */
    deviceAlarm?: string;
    deviceAlarms?: string[];
    onDeviceAlarmChoose?: (alarm: string) => void;
    language?: string;
    /** Show missing-recipient messages although the field was not touched (a save was tried). */
    showErrors: boolean;
    onChange: (action: Action) => void;
    onRemove: () => void;
    onTest: () => void;
}) {
    const { action, index, fieldId, t } = properties;
    const isEmail = action instanceof EmailNotification;
    const isSMS = action instanceof SMSNotification;

    const subjectField = useRef<PlaceholderTextFieldHandle>(null);
    const contentField = useRef<PlaceholderTextFieldHandle>(null);
    const [recipientTouched, setRecipientTouched] = useState(false);
    const [pickerOpenFor, setPickerOpenFor] = useState<PlaceholderTarget | null>(null);

    if (!isEmail && !isSMS) {
        return null;
    }

    const emailAction = action as EmailNotification;
    const smsAction = action as SMSNotification;
    const subject = (isEmail ? emailAction.subject : '') ?? '';
    const content = ((isEmail ? emailAction.content : smsAction.content) ?? '') as string;
    const subjectLabel = t(`${ALERT_TEXT_NAMESPACE}subject`);
    const contentLabel = t(`${ALERT_TEXT_NAMESPACE}${isEmail ? 'content' : 'message'}`);
    const typeLabel = t(`${ALERT_TEXT_NAMESPACE}${isEmail ? 'emailLabel' : 'sms'}`);
    const TypeIcon = isEmail ? Mail : MessageSquare;

    const updateField = (field: PlaceholderTarget, value: string) =>
        properties.onChange(Action.unmarshal({ ...(action as any), [field]: value }));

    const insertPlaceholder = (field: PlaceholderTarget, name: string, keepOpen = false) =>
        (field === 'subject' ? subjectField : contentField).current?.insertText(placeholderTag(name), { keepFocus: keepOpen });

    const describe = (name: string): BadgeLook => placeholderBadge(name, t);

    const recipient = isEmail ? (emailAction.to ?? '') : (smsAction.phoneNumber ?? '');
    const recipientProblem = recipient.trim() === ''
        ? (isEmail ? 'recipientRequired' : 'phoneRequired')
        : isEmail
            ? (recipient.split(',').every((address) => /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(address.trim()))
                ? null
                : 'recipientInvalid')
            : (/^\+?[\d\s()/-]{6,}$/.test(recipient.trim()) ? null : 'phoneInvalid');
    const showRecipientProblem = recipientProblem !== null && (recipientTouched || properties.showErrors);
    const recipientErrorId = `${fieldId}-recipient-error-${index}`;

    const fieldHeader = (labelId: string, label: string, target: PlaceholderTarget) => (
        <div className="flex min-h-7 items-center justify-between gap-2">
            <Label
                id={labelId}
                onClick={() => (target === 'subject' ? subjectField : contentField).current?.focus()}
            >
                {label}
            </Label>
            <PlaceholderPicker
                t={t}
                fieldLabel={label}
                noUplink={properties.noUplink}
                language={properties.language}
                usedNames={placeholdersOf(target === 'subject' ? subject : content)}
                open={pickerOpenFor === target}
                onOpenChange={(open) => setPickerOpenFor(open ? target : null)}
                onInsert={(name, keepOpen) => insertPlaceholder(target, name, keepOpen)}
            />
        </div>
    );

    const unknown = unknownPlaceholdersOfAction(action);
    const subjectLabelId = `${fieldId}-subject-label-${index}`;
    const contentLabelId = `${fieldId}-content-label-${index}`;
    const recipientId = `${fieldId}-${isEmail ? 'to' : 'phone'}-${index}`;

    return (
        <div className="min-w-0 rounded-lg border bg-card">
            <div className="flex min-w-0 items-center gap-2 border-b px-3 py-2">
                <TypeIcon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-semibold">{typeLabel}</span>
                {properties.showErrors && !action.isCompletelyDefined() && (
                    <span role="alert" className="min-w-0 truncate text-xs text-destructive">
                        {t(`${ALERT_TEXT_NAMESPACE}incompleteDefinition`)}
                    </span>
                )}
                <div className="ml-auto flex shrink-0 items-center gap-1">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!action.isCompletelyDefined() || properties.testing}
                        onClick={properties.onTest}
                    >
                        {properties.testing && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        {properties.testing ? t(`${ALERT_TEXT_NAMESPACE}sending`) : t(`${ALERT_TEXT_NAMESPACE}test`)}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t(`${ALERT_TEXT_NAMESPACE}removeAction`)}
                        title={t(`${ALERT_TEXT_NAMESPACE}removeAction`)}
                        onClick={properties.onRemove}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <div className="grid min-w-0 gap-4 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
                <div className="flex min-w-0 flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={recipientId}>
                            {t(`${ALERT_TEXT_NAMESPACE}${isEmail ? 'addressees' : 'phoneNumber'}`)}
                        </Label>
                        <Input
                            id={recipientId}
                            type="text"
                            inputMode={isEmail ? 'email' : 'tel'}
                            value={recipient}
                            aria-invalid={showRecipientProblem}
                            aria-describedby={showRecipientProblem ? recipientErrorId : undefined}
                            onBlur={() => setRecipientTouched(true)}
                            onChange={(event) => properties.onChange(
                                Action.unmarshal({ ...(action as any), [isEmail ? 'to' : 'phoneNumber']: event.target.value }),
                            )}
                        />
                        {showRecipientProblem && recipientProblem && (
                            <p id={recipientErrorId} role="alert" className="text-xs text-destructive">
                                {t(`${ALERT_TEXT_NAMESPACE}${recipientProblem}`)}
                            </p>
                        )}
                    </div>
                    {isEmail && (
                        <div className="flex flex-col gap-1">
                            {fieldHeader(subjectLabelId, subjectLabel, 'subject')}
                            <PlaceholderTextField
                                ref={subjectField}
                                id={`${fieldId}-subject-${index}`}
                                labelledBy={subjectLabelId}
                                value={subject}
                                describe={describe}
                                onShortcut={() => setPickerOpenFor('subject')}
                                onChange={(value) => updateField('subject', value)}
                            />
                        </div>
                    )}
                    <div className="flex flex-col gap-1">
                        {fieldHeader(contentLabelId, contentLabel, 'content')}
                        <PlaceholderTextField
                            ref={contentField}
                            id={`${fieldId}-content-${index}`}
                            labelledBy={contentLabelId}
                            multiline
                            value={content}
                            describe={describe}
                            onShortcut={() => setPickerOpenFor('content')}
                            onChange={(value) => updateField('content', value)}
                        />
                    </div>
                    {unknown.map((problem) => (
                        <p key={problem.name} role="alert" className="text-sm text-destructive">
                            {unknownPlaceholderMessage(problem, t)}
                        </p>
                    ))}
                </div>
                <AlertMessagePreview
                    t={t}
                    kind={isEmail ? 'email' : 'sms'}
                    language={properties.language}
                    deviceAlarm={properties.deviceAlarm}
                    deviceAlarms={properties.deviceAlarms}
                    onDeviceAlarmChoose={properties.onDeviceAlarmChoose}
                    subject={subject}
                    content={content}
                />
            </div>
        </div>
    );
}
